<div class="add-plan-dialog-container">
  <!-- Modal Header (Figma: back + title + close) -->
  <div class="modal-header">
    <div class="modal-header-start">
      <button
        mat-icon-button
        type="button"
        class="back-button mat-icon-button-sm"
        (click)="onCancel()"
        [disabled]="loading()"
        aria-label="Back"
      >
        <mat-icon class="mat-icon-sm">arrow_back</mat-icon>
      </button>
      <h2 class="modal-title">New Routine</h2>
    </div>
    <button
      mat-icon-button
      class="close-button mat-icon-button-sm"
      (click)="onCancel()"
      [disabled]="loading()"
      aria-label="Close"
    >
      <mat-icon class="mat-icon-sm">close</mat-icon>
    </button>
  </div>

  <!-- Dialog Content -->
  <div mat-dialog-content class="dialog-content">
    <!-- Loading State -->
    @if (loading() && !routineForm.value.name) {
      <div class="loading-container">
        <mat-spinner diameter="32"></mat-spinner>
        <p class="loading-text">Loading project data...</p>
      </div>
    }

    <!-- Error Message -->
    @if (error()) {
      <div class="error-container">
        <mat-icon class="error-icon">error</mat-icon>
        <span class="error-message">{{ error() }}</span>
      </div>
    }

    <!-- Form -->
    @if (!loading() || routineForm.value.name) {
      <form [formGroup]="routineForm" (ngSubmit)="onSave()">
        <div class="content-layout">
          <!-- Left Column - General and Plan Settings -->
          <div class="left-column">
            <!-- General Section -->
            <div class="section general-section">
              <h3 class="section-title">General</h3>
              <div class="form-row form-row-general">
                <div class="form-field field-routine-name">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Routine Name</mat-label>
                    <input
                      matInput
                      formControlName="name"
                      placeholder="Routine Name"
                    />
                  </mat-form-field>
                  @if (getFormFieldError("name")) {
                    <div class="field-error">
                      {{ getFormFieldError("name") }}
                    </div>
                  }
                </div>
                <div class="form-field field-runs-as">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Runs as User</mat-label>
                    <mat-select formControlName="runsAsId">
                      <mat-select-trigger>
                        {{
                          getUserNameById(routineForm.get("runsAsId")?.value)
                        }}
                      </mat-select-trigger>
                      @for (user of projectUsers(); track user.userId) {
                        <mat-option [value]="user.userId">
                          <div class="user-option">
                            <mat-icon class="user-icon">person</mat-icon>
                            <span>{{ user.userName }}</span>
                          </div>
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  @if (getFormFieldError("runsAsId")) {
                    <div class="field-error">
                      {{ getFormFieldError("runsAsId") }}
                    </div>
                  }
                </div>
              </div>
              <div class="form-row form-row-general">
                <div class="form-field field-workflow">
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Workflow</mat-label>
                    <mat-select formControlName="workflowId">
                      <mat-select-trigger>
                        {{
                          getWorkflowNameById(
                            routineForm.get("workflowId")?.value
                          ) || "Select workflow"
                        }}
                      </mat-select-trigger>
                      @for (
                        workflow of projectWorkflows;
                        track workflow.workflowId
                      ) {
                        <mat-option [value]="workflow.workflowId">
                          <span>{{ workflow.name }}</span>
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  @if (getFormFieldError("workflowId")) {
                    <div class="field-error">
                      {{ getFormFieldError("workflowId") }}
                    </div>
                  }
                </div>
                @if (!routineForm.get("useHead")?.value) {
                  <div class="form-field version-field">
                    <mat-form-field appearance="outline" class="full-width">
                      <mat-label>Version</mat-label>
                      <mat-select formControlName="versionId">
                        <mat-select-trigger>
                          {{
                            getVersionNameById(
                              routineForm.get("versionId")?.value
                            ) || "Select version"
                          }}
                        </mat-select-trigger>
                        @for (
                          version of projectVersions;
                          track version.versionId
                        ) {
                          <mat-option [value]="version.versionId">
                            <div class="version-option">
                              <span class="version-number"
                                >Version {{ getVersionDisplay(version) }}</span
                              >
                              <span class="version-date">{{
                                formatVersionDate(version.createdAt)
                              }}</span>
                            </div>
                          </mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                    @if (getFormFieldError("versionId")) {
                      <div class="field-error">
                        {{ getFormFieldError("versionId") }}
                      </div>
                    }
                    @if (hasOnlyOneVersion) {
                      <div class="info-message">
                        <mat-icon class="info-icon">info</mat-icon>
                        <span
                          >Version is readonly because only one version
                          exists</span
                        >
                      </div>
                    }
                  </div>
                }
              </div>
              <!-- HEAD Version Execution — only for manual (unscheduled) routines -->
              @if (
                !routineForm.get("cronScheduleType")?.value ||
                routineForm.get("cronScheduleType")?.value === "manual"
              ) {
                <div class="form-row">
                  <div class="form-field full-width">
                    <mat-checkbox formControlName="useHead" color="primary">
                      Execute against HEAD version (development)
                    </mat-checkbox>
                    <div class="info-message">
                      <mat-icon class="info-icon">info</mat-icon>
                      <span
                        >When enabled, manual runs use the current development
                        version instead of a published version</span
                      >
                    </div>
                  </div>
                </div>
              }
              @if (projectVersions.length === 0) {
                @if (
                  !routineForm.get("cronScheduleType")?.value ||
                  routineForm.get("cronScheduleType")?.value === "manual"
                ) {
                  @if (routineForm.get("useHead")?.value) {
                    <div
                      class="info-message full-width info-message--head-auto"
                    >
                      <mat-icon class="info-icon">info</mat-icon>
                      <span
                        >No published versions exist yet — "Execute against HEAD
                        version" has been enabled automatically. You can publish
                        a version later to bind this routine to a stable
                        release.</span
                      >
                    </div>
                  } @else {
                    <div class="info-message full-width info-message--warning">
                      <mat-icon class="info-icon">warning</mat-icon>
                      <span
                        >No published versions available. Enable "Execute
                        against HEAD version" above, or publish a version first
                        before creating this routine.</span
                      >
                    </div>
                  }
                } @else {
                  <div class="info-message full-width info-message--warning">
                    <mat-icon class="info-icon">warning</mat-icon>
                    <span
                      >No published versions available. Scheduled routines
                      require a published version — publish a version first
                      before creating this routine.</span
                    >
                  </div>
                }
              }
              @if (projectWorkflows.length === 0 && !loading()) {
                <div class="info-message full-width">
                  <mat-icon class="info-icon">info</mat-icon>
                  <span
                    >No workflows available. Create a workflow first before
                    creating routines.</span
                  >
                </div>
              }
            </div>
            <!-- Routine Settings Section -->
            <div class="section">
              <h3 class="section-title">Routine Settings</h3>
              <div class="form-field">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>Schedule</mat-label>
                  <mat-select
                    formControlName="cronScheduleType"
                    (selectionChange)="onCronScheduleChange($event)"
                  >
                    @for (option of cronScheduleOptions; track option.value) {
                      <mat-option [value]="option.value">
                        <div class="schedule-option">
                          <span class="option-label">{{ option.label }}</span>
                          <span class="option-description">{{
                            option.description
                          }}</span>
                        </div>
                      </mat-option>
                    }
                  </mat-select>
                  <mat-icon matSuffix>schedule</mat-icon>
                </mat-form-field>
              </div>
              <!-- Custom Cron Input -->
              @if (showCustomCronInput) {
                <div class="custom-cron-section">
                  <div class="schedule-input-row">
                    <span class="schedule-label">Set schedule:</span>
                    <div class="cron-input-field">
                      <mat-form-field appearance="outline" class="cron-field">
                        <input
                          matInput
                          formControlName="crontab"
                          placeholder="* * * * *"
                          class="cron-input"
                        />
                      </mat-form-field>
                    </div>
                  </div>
                  @if (
                    routineForm.get("crontab")?.value &&
                    !validateCustomCron(routineForm.get("crontab")?.value)
                  ) {
                    <div class="field-error">
                      {{
                        getCronValidationError(
                          routineForm.get("crontab")?.value
                        )
                      }}
                    </div>
                  }
                </div>
              }
            </div>
            <!-- Arguments Section -->
            <div class="section">
              <h3 class="section-title">Arguments (JSON)</h3>
              <div class="info-message">
                <mat-icon class="info-icon">info</mat-icon>
                <span
                  >Merged into every job's arguments: schema defaults &lt;
                  routine arguments &lt; per-run arguments</span
                >
              </div>
              <app-code-editor
                [content]="argumentsContent"
                (updateContent)="onArgumentsContentChange($event)"
                (startEdit)="onArgumentsStartEdit()"
                mode="json"
                [minimumHeight]="120"
                class="full-width"
              >
              </app-code-editor>
              @if (getFormFieldError("arguments")) {
                <div class="field-error">
                  {{ getFormFieldError("arguments") }}
                </div>
              }
            </div>
          </div>
          <!-- Right Column - Routine Description -->
          <div class="right-column">
            <div class="description-section">
              <h3 class="section-title">Routine Description</h3>
              <div class="form-field">
                <mat-form-field
                  appearance="outline"
                  class="full-width description-field"
                >
                  <mat-label>Description</mat-label>
                  <textarea
                    matInput
                    formControlName="description"
                    placeholder="Describe what this routine does"
                    rows="6"
                    class="description-textarea"
                  ></textarea>
                </mat-form-field>
                @if (getFormFieldError("description")) {
                  <div class="field-error">
                    {{ getFormFieldError("description") }}
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </form>
    }
  </div>

  <!-- Modal Actions -->
  <div mat-dialog-actions class="modal-actions">
    <button class="cancel-button" (click)="onCancel()" [disabled]="loading()">
      Cancel
    </button>
    <button
      class="create-button"
      (click)="onSave()"
      [disabled]="loading() || routineForm.invalid"
    >
      @if (loading()) {
        <mat-spinner diameter="20" class="button-spinner"></mat-spinner>
      }
      <span>{{ loading() ? "Creating..." : "Create" }}</span>
    </button>
  </div>
</div>
